import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { RemapPreview } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { formatAmount, formatHours } from '@/lib';

/**
 * Confirmation before a task moves to another contract (D17). Roles carry
 * across contracts by identity, but a T&M card may not hold every role — a
 * line whose role the target cannot price is orphaned (it stays, at 0, BR-11)
 * — and the task's planned revenue changes with the contract's pricing. Both
 * are shown before anything is written; the dialog is pure over a
 * `previewContractRemap` result so the copy is assertable.
 */
export type ContractRemapDialogProps = {
  open: boolean;
  /** The target's label, or the "no contract" wording. */
  targetLabel: string;
  preview: RemapPreview;
  currency: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ContractRemapDialog = ({
  open,
  targetLabel,
  preview,
  currency,
  busy = false,
  onConfirm,
  onCancel,
}: ContractRemapDialogProps) => {
  const { t } = useTranslation('pmPlanning');
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      data-automation-id="contract-remap-dialog"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('contractRemap.Title', { target: targetLabel })}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {preview.orphanedLines.length > 0 ? (
          <Typography variant="bodyRegular" color="text.secondary" component="div">
            {t('contractRemap.Orphaned', { count: preview.orphanedLines.length })}
            <ul
              data-automation-id="contract-remap-orphans"
              style={{ margin: '8px 0 0', paddingLeft: 20 }}
            >
              {preview.orphanedLines.map((line) => (
                <li key={line.allocationId}>
                  {t('contractRemap.OrphanLine', {
                    role: line.roleName,
                    hours: formatHours(line.hours),
                  })}
                </li>
              ))}
            </ul>
          </Typography>
        ) : null}
        <Typography
          variant="bodyRegular"
          color="text.secondary"
          data-automation-id="contract-remap-amounts"
        >
          {t('contractRemap.Amounts', {
            before: formatAmount(preview.amountBefore),
            after: formatAmount(preview.amountAfter),
            currency,
          })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={busy}
          color="inherit"
          data-automation-id="contract-remap-cancel"
        >
          {t('contractRemap.Cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          data-automation-id="contract-remap-confirm"
        >
          {t('contractRemap.Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
